import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  MetricCard,
  PageHeader,
  SkeletonRows,
  statusTone,
} from "../components/ui";
import { dashboardService, type DashboardSummary } from "../services/dashboard.service";
import { formatNumber, formatRelative } from "../lib/format";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dashboardService
      .summary()
      .then(setSummary)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const metrics = summary?.metrics;

  return (
    <>
      <PageHeader
        title={`Olá, ${user?.name ?? ""}`}
        description={`Perfil: ${user?.role_label ?? "—"} · Organizações: ${
          user?.organizations?.map((o) => o.name).join(", ") || "—"
        }`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate("/projects/new/wizard")}>Criar sistema com IA</Button>
            <Button variant="ghost" onClick={() => navigate("/projects/new")}>
              Novo projeto
            </Button>
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{error}</p>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard label="Projetos" value={formatNumber(metrics?.projects_total ?? 0)} loading={loading} />
        <MetricCard label="Ativos" value={formatNumber(metrics?.projects_active ?? 0)} loading={loading} />
        <MetricCard
          label="Execuções IA (30d)"
          value={formatNumber(metrics?.ai_runs_30d ?? 0)}
          loading={loading}
        />
        <MetricCard
          label="Consumo (30d)"
          value={formatNumber(metrics?.usage_30d ?? 0)}
          hint="unidades registradas"
          loading={loading}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card
          title="Projetos recentes"
          action={
            <Link to="/projects" className="font-mono text-xs text-ink-muted hover:text-brand">
              ver todos
            </Link>
          }
        >
          {loading ? (
            <SkeletonRows />
          ) : summary && summary.recent_projects.length > 0 ? (
            <ul className="space-y-3">
              {summary.recent_projects.map((project) => (
                <li key={project.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-ink">{project.name}</p>
                    <p className="font-mono text-xs text-ink-muted">
                      {formatRelative(project.last_activity_at)}
                    </p>
                  </div>
                  <Badge tone={statusTone(project.status)}>{project.status}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Nenhum projeto ainda"
              description="Crie o primeiro projeto para começar."
              action={<Button size="sm" onClick={() => navigate("/projects/new")}>Novo projeto</Button>}
            />
          )}
        </Card>

        <Card title="Últimos deploys">
          {loading ? (
            <SkeletonRows rows={2} />
          ) : summary && summary.recent_deployments.length > 0 ? (
            <ul className="space-y-3">
              {summary.recent_deployments.map((deployment) => (
                <li key={deployment.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm">{deployment.project ?? "—"}</p>
                    <p className="font-mono text-xs text-ink-muted">
                      {deployment.environment ?? "—"} · {formatRelative(deployment.created_at)}
                    </p>
                  </div>
                  <Badge tone={statusTone(deployment.status)}>{deployment.status}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Nenhum deploy registrado"
              description="Deploys aparecem aqui quando o módulo de publicação for ativado."
            />
          )}
        </Card>

        <Card title="Erros recentes" subtitle="Execuções de IA com falha nos últimos 30 dias">
          {loading ? (
            <SkeletonRows rows={2} />
          ) : summary && summary.recent_errors.length > 0 ? (
            <ul className="space-y-3">
              {summary.recent_errors.map((item) => (
                <li key={item.id} className="rounded-md border border-danger/30 bg-danger/5 p-3">
                  <p className="text-sm text-danger">{item.message}</p>
                  <p className="mt-1 font-mono text-xs text-ink-muted">
                    {item.project ?? "—"} · {formatRelative(item.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Sem erros registrados" description="Nada falhou por aqui." />
          )}
        </Card>

        <Card title="Consumo" subtitle="Resumo dos últimos 30 dias">
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="font-mono text-[11px] uppercase text-ink-muted">Execuções IA</dt>
              <dd className="mt-1 text-lg">{formatNumber(metrics?.ai_runs_30d ?? 0)}</dd>
            </div>
            <div>
              <dt className="font-mono text-[11px] uppercase text-ink-muted">Falhas</dt>
              <dd className="mt-1 text-lg">{formatNumber(metrics?.ai_runs_failed_30d ?? 0)}</dd>
            </div>
            <div>
              <dt className="font-mono text-[11px] uppercase text-ink-muted">Deploys</dt>
              <dd className="mt-1 text-lg">{formatNumber(metrics?.deployments_total ?? 0)}</dd>
            </div>
            <div>
              <dt className="font-mono text-[11px] uppercase text-ink-muted">Unidades</dt>
              <dd className="mt-1 text-lg">{formatNumber(metrics?.usage_30d ?? 0)}</dd>
            </div>
          </dl>
        </Card>
      </div>
    </>
  );
}
